/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSetDistinct';

const ActionSetDistinctManager: ActionManagerFn<'ActionSetDistinct'> = (
  platform: Platform,
): ActionManager<'ActionSetDistinct'> => {
  return {
    ...baseActionManager(platform, 'ActionSetDistinct'),
    defaultValue,
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (
        parent.type === 'ActionQuery' || parent.type === 'TriggerBeforeQuery'
      );
    },
    props: {
      ...baseActionProps,
    },
    name: 'Set Distinct',
    description: `**Overview**<br/>
The Set Distinct action is a query optimization component designed to eliminate duplicate records from a data retrieval operation. It functions as a modifier for the standard database query, instructing the backend engine to filter the result set so that every returned row is unique.<br/><br/>
This component corresponds to the SQL DISTINCT keyword. It is strictly an architectural element that cannot exist independently; it must be placed as a child of an ActionQuery or TriggerBeforeQuery. When added, it ensures that even if multiple records share the same values for the queried attributes, only one instance of that combination is returned to the application.

**Key Features**<br/>
* **Duplicate Elimination:** Automatically filters out redundant data rows at the database level, ensuring the application receives a clean, unique list of items.
* **Strict Parent Scope:** Enforces usage validity via isAllowedInsideParent, restricting the component to function only as a child of ActionQuery or TriggerBeforeQuery.
* **Zero Configuration:** Inherits all behavior from the base action structure and requires no specific properties or parameters to function. Its mere presence acts as a boolean flag enabling the "Distinct" mode.

**Properties**<br/>
* **Base Properties:** This component utilizes ...baseActionProps exclusively, accepting only the standard configuration options available to all actions.

**Use Cases**<br/>
* **Dropdown Population:** Querying a "Customers" table to populate a "City" dropdown. Without this action, "New York" might appear 500 times; with this action, it appears once.
* **Category Listing:** Fetching a list of unique product categories currently present in an "Orders" table to build a filter menu.
* **Data Auditing:** Retrieving a list of unique error codes from a large system log to identify which distinct types of failures are occurring.`,
  };
};

export default ActionSetDistinctManager;
